/**
 * Post Content Char Counter Handler
 *
 * Keeps post content character counters in sync with textarea input length.
 */

import { postContentCharCounterForm } from "./post-content-char-counter-form";
import { PostContentCharCounterListener } from "./post-content-char-counter-listener";
import { PostSharedUtils } from "../util/post-shared-utils";

class PostContentCharCounterHandler {
    public static THRESHOLD_LOW_MAX = 100;
    public static THRESHOLD_MIDDLE_MAX = 200;
    public static THRESHOLD_HIGH_MIN = 201;
    public static URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<]+/gi;

    public static init(root: HTMLElement | Document = document): void {
        const textareas = postContentCharCounterForm.getTextareas(root);

        textareas.forEach((textarea) => {
            if (!(textarea instanceof HTMLTextAreaElement)) return;
            if (textarea.dataset.charCounterBound === "1") {
                PostContentCharCounterHandler.updateCounter(textarea);
                return;
            }

            textarea.dataset.charCounterBound = "1";
            PostContentCharCounterListener.bindTextareaInput(textarea, () =>
                PostContentCharCounterHandler.updateCounter(textarea),
            );
            PostContentCharCounterHandler.updateCounter(textarea);
        });
    }

    public static updateCounter(textarea: HTMLTextAreaElement): void {
        const container = postContentCharCounterForm.getCounter(
            postContentCharCounterForm.getBoardContent(textarea) || undefined,
        );
        if (!(container instanceof HTMLElement)) return;

        const countEl = postContentCharCounterForm.getCount(container);
        if (!(countEl instanceof HTMLElement)) return;

        const snsLinks = PostSharedUtils.getNormalizedSnsLinksFromForm(textarea.form);
        const textLength = PostSharedUtils.getNormalizedPostContentLength(textarea.value || "", snsLinks);
        countEl.textContent = String(textLength);

        container.dataset.count = String(textLength);

        const progressRatio =
            Math.min(textLength, PostContentCharCounterHandler.THRESHOLD_HIGH_MIN) /
            PostContentCharCounterHandler.THRESHOLD_HIGH_MIN;
        const progressPercent = `${Math.round(progressRatio * 100)}%`;
        container.style.setProperty("--post-content-progress", progressPercent);

        const progressFill = postContentCharCounterForm.getProgressFill(container);
        if (progressFill instanceof HTMLElement) {
            progressFill.style.width = progressPercent;
            const progressState =
                textLength >= PostContentCharCounterHandler.THRESHOLD_HIGH_MIN
                    ? "high"
                    : textLength > PostContentCharCounterHandler.THRESHOLD_LOW_MAX
                      ? "middle"
                      : "low";
            progressFill.classList.remove("low", "middle", "high");
            progressFill.classList.add(progressState);
        }

        const shortBadge = postContentCharCounterForm.getThreshold(container, "100");
        if (shortBadge instanceof HTMLElement) {
            shortBadge.classList.toggle("is-reached", textLength >= PostContentCharCounterHandler.THRESHOLD_LOW_MAX);
        }

        const longBadge = postContentCharCounterForm.getThreshold(container, "201");
        if (longBadge instanceof HTMLElement) {
            longBadge.classList.toggle("is-reached", textLength >= PostContentCharCounterHandler.THRESHOLD_HIGH_MIN);
        }
    }

    public static getPlainTextLength(raw: string): number {
        return PostSharedUtils.getNormalizedPostContentLength(raw);
    }

    public static extractCountableTextFromHtml(html: string): string {
        const parser = document.createElement("div");
        parser.innerHTML = html;

        postContentCharCounterForm.getEmbeds(parser).forEach((element) => element.remove());

        const text = parser.textContent || "";
        return PostContentCharCounterHandler.removeUrls(text);
    }

    public static removeUrls(text: string): string {
        return String(text).replace(PostContentCharCounterHandler.URL_PATTERN, "");
    }

    public static stripHtml(html: string): string {
        const parser = document.createElement("div");
        parser.innerHTML = html;
        return parser.textContent || "";
    }
}

export { PostContentCharCounterHandler };
